import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseDialog from './BaseDialog';
import VoiceSelector from '../VoiceSelector';
import { generateTTS } from '../../utils/api';

// 导入Voice类型定义
import type { Voice } from '../VoiceSelector';

// 定义VoiceSynthesisDialog的props接口
interface VoiceSynthesisDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
  sceneNumber: number;
  initialNarration: string;
  initialVoice: string;
  voices: Voice[];
  initialVoiceSpeed?: number;
  onUpdateNarration: (sceneId: string, newNarration: string) => void;
  onUpdateAudio: (sceneId: string, audioUrl: string, duration?: number) => void;
  onUpdateNarrationSilent?: (sceneId: string, newNarration: string) => void;
  onUpdateVoice?: (voice: string) => void;
  onUpdateVoiceSpeed?: (speed: number) => void;
  onSetDialogOperating?: (sceneId: string, isOperating: boolean) => void;
  onShowToast: (message: string) => void;
}

/**
 * 语音合成对话框组件
 * - 继承BaseDialog的基础样式和布局
 * - 专门用于文本到语音的合成功能
 * - 包含旁白编辑、音色选择和语音合成
 */
const VoiceSynthesisDialog: React.FC<VoiceSynthesisDialogProps> = ({
  isOpen,
  onClose,
  sceneId,
  sceneNumber,
  initialNarration,
  initialVoice,
  voices,
  initialVoiceSpeed = 1.0,
  onUpdateAudio,
  onUpdateNarrationSilent,
  onUpdateVoice,
  onUpdateVoiceSpeed,
  onSetDialogOperating,
  onShowToast
}) => {
  const { t } = useTranslation();
  const [narration, setNarration] = useState(initialNarration);
  const [selectedVoice, setSelectedVoice] = useState(initialVoice);
  const [voiceSpeed, setVoiceSpeed] = useState(initialVoiceSpeed);

  // 当对话框打开时，同步所有状态
  useEffect(() => {
    if (isOpen) {
      setNarration(initialNarration);
      setSelectedVoice(initialVoice);
      setVoiceSpeed(initialVoiceSpeed);
    }
  }, [isOpen, initialNarration, initialVoice, initialVoiceSpeed]);

  // 处理生成语音
  const handleGenerateVoice = async () => {
    // 不要在这里更新，避免触发useEffect
    // onUpdateNarration(sceneId, narration);

    // 找到当前选中的voice对象，获取voice_type
    const selectedVoiceObj = voices.find(v => v.voice_id === selectedVoice);
    const voiceType = selectedVoiceObj?.voice_type || selectedVoice;

    // 创建语音合成请求参数
    const requestParams = {
      text: narration,
      voice_name: voiceType,
      audio_format: 'mp3' as const,
      volume: 1.0,
      speed: voiceSpeed,
      emotion: 'neutral' as const,
      response_type: 'url' as const
    };

    // 显示toast提示
    const message = t('toast.voice.generatingVoice', {
      text: requestParams.text.slice(0, 20),
      voice: selectedVoiceObj?.name || selectedVoice,
      format: requestParams.audio_format
    });
    onShowToast(message);

    console.log('🔥 [VoiceSynthesisDialog] 开始生成语音', { sceneId, text: requestParams.text.slice(0, 30), voiceType });

    // 设置对话框操作标志，阻止此分镜的自动生成
    if (onSetDialogOperating) {
      onSetDialogOperating(sceneId, true);
    }

    try {
      // 调用语音合成接口
      console.log('🚀 [VoiceSynthesisDialog] 调用 generateTTS API');
      const response = await generateTTS(requestParams);

      if (response.code === 200) {
        let audioUrl = response.data?.audio_url;

        // 如果返回的是base64格式的音频，转换为Blob URL
        if (!audioUrl && response.data?.audio_base64) {
          const base64Data = response.data.audio_base64;
          const blob = new Blob([Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))], { type: 'audio/mp3' });
          audioUrl = URL.createObjectURL(blob);
        }

        if (audioUrl) {
          // 获取音频时长（API返回的是毫秒，需要转换为秒）
          const duration = response.data?.duration ? response.data.duration / 1000 : undefined;

          // 直接更新音频
          onUpdateAudio(sceneId, audioUrl, duration);

          // 静默更新旁白，不触发useEffect
          if (onUpdateNarrationSilent) {
            onUpdateNarrationSilent(sceneId, narration);
          }

          // 显示成功提示
          onShowToast(t('toast.voice.regenerateSuccess'));

          // 关闭对话框
          onClose();
        }
      }
    } catch (error) {
      console.error('TTS re-generation failed:', error);
      // 显示错误提示
      const errorMessage = t('toast.voice.regenerateError', { error: error instanceof Error ? error.message : t('toast.common.unknownError') });
      onShowToast(errorMessage);
    } finally {
      // 无论成功还是失败，都重置对话框操作标志
      if (onSetDialogOperating) {
        onSetDialogOperating(sceneId, false);
      }
    }
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialog.voiceSynthesis.title', { sceneNumber })}
      width={800}
      height={600}
    >
      <div className="flex flex-col gap-4">
        {/* 旁白编辑区域 */}
        <div className="flex flex-col gap-2 relative">
          <label className="text-sm text-gray-400">{t('dialog.voiceSynthesis.voiceText')}</label>
          <textarea
            className="p-4 bg-gray-900/80 rounded-lg border border-gray-700/50 whitespace-pre-wrap text-sm text-white resize-y min-h-[100px] pr-16"
            value={narration}
            onChange={(e) => {
              const newNarration = e.target.value;
              setNarration(newNarration);
              // 实时静默更新旁白到sceneList
              if (onUpdateNarrationSilent) {
                onUpdateNarrationSilent(sceneId, newNarration);
              }
            }}
            placeholder={t('dialog.voiceSynthesis.textPlaceholder')}
          />
          {/* 生成按钮 */}
          <button
            className="absolute bottom-2 right-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white p-2 hover:shadow-lg transition-all transform hover:scale-105"
            onClick={handleGenerateVoice}
            title={t('dialog.voiceSynthesis.generateVoice')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>

        {/* 音色选择区域 */}
        <div className="ml-4">
          <VoiceSelector
            voices={voices}
            selectedVoice={selectedVoice}
            onVoiceSelect={(newVoice) => {
              setSelectedVoice(newVoice);
              if (onUpdateVoice) {
                onUpdateVoice(newVoice);
              }
            }}
            voiceSpeed={voiceSpeed}
            onSpeedChange={(newSpeed) => {
              setVoiceSpeed(newSpeed);
              if (onUpdateVoiceSpeed) {
                onUpdateVoiceSpeed(newSpeed);
              }
            }}
          />
        </div>

        {/* 语音参数说明 */}
        <div className="ml-4 mt-2">
          <div className="text-xs text-gray-500 space-y-1">
            <p>• {t('dialog.voiceSynthesis.currentVoice')}: {voices.find(v => v.voice_id === selectedVoice)?.name || '未知'}</p>
            <p>• {t('dialog.voiceSynthesis.audioFormat')}: MP3</p>
            <p>• {t('dialog.voiceSynthesis.speechRate')}: {t('dialog.voiceSynthesis.standard')}</p>
            <p>• {t('dialog.voiceSynthesis.emotion')}: {t('dialog.voiceSynthesis.neutral')}</p>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};

export default VoiceSynthesisDialog;